import { DownloadIcon } from "@chakra-ui/icons";
import { useState } from "react";
import { MdOutlineEdit } from "react-icons/md";
import { useIntl } from "react-intl";

import { FileIcon, MenuAction, TableAction, TrashIcon, WrenchIcon } from "@nens/ranalib";
import { downloadFile } from "@nens/shared-utils";

import { getProjectTabQueryParam } from "@/features/project";
import { useProjectActionAllowed } from "@/hooks/use-action-allowed-in-project";
import { convertFileIdToQueryParams } from "@/utils/convert-file-id-to-query-params";
import { downloadMultipleFileReferences, downloadMultipleFiles } from "@/utils/downloads";
import { getNameFromFileId } from "@/utils/get-name-from-file-id";

import { getFiles, getIsEditable, getIsPublic, getMaps } from "../../../store/selectors";
import { usePublicationStore } from "../../../store/store";
import { CompleteFile } from "../../../types/file-reference-with-file";
import { Layer } from "../../../types/publication";

type OpenModal =
  | {
      type: "file-version" | "file-reference-name";
      file: CompleteFile;
    }
  | {
      type: "file-remove-confirmation";
      files: CompleteFile[];
    }
  | { type: "file-add" };

export function usePublicationDetailFileList() {
  const intl = useIntl();
  const { canWriteProject } = useProjectActionAllowed();
  const isEditable = usePublicationStore(getIsEditable);

  const [openModal, setOpenModal] = useState<OpenModal | null>(null);

  const isPublic = usePublicationStore(getIsPublic);
  const publicationFiles = usePublicationStore(getFiles);
  const publicationMaps = usePublicationStore(getMaps);
  const removeFileByPath = usePublicationStore((store) => store.removeFileByPath);

  const removeClicked = (filePaths: string[]) => {
    const files = publicationFiles.filter((file) => filePaths.includes(file.path));

    // Check if file is not used in any layers
    const allLayersInPublication = publicationMaps.flatMap((map) =>
      map.layers.flatMap((item) => (item.type === "folder" ? item.layers : [item]))
    ) as Layer[];

    // Handle both snake_case and camelCase formats
    const isFileUsedInLayers = allLayersInPublication.some((layer) =>
      filePaths.includes(layer.filePath)
    );

    isFileUsedInLayers
      ? setOpenModal({ type: "file-remove-confirmation", files })
      : filePaths.forEach((path) => removeFileByPath(path));
  };

  const getRowActions = (path: string, ref: string | undefined): MenuAction[] => {
    const fullFile = publicationFiles.find((file) => file.path === path);
    if (!fullFile) return [];

    const actions: MenuAction[] = [];

    // For now we just use the download URL directly even though it may expire,
    // because we don't have a projectId here so can't use other download functions
    // (see https://github.com/nens/rana/issues/2557 )
    if (fullFile.file.url && fullFile.file.id) {
      actions.push({
        label: intl.formatMessage({
          id: "action.download_file",
          defaultMessage: "Download file",
        }),
        action: () =>
          downloadFile(fullFile.file.url, getNameFromFileId(fullFile.file.id), "_blank"), // Open in new tab to avoid route-blocker blocking the download
        icon: <DownloadIcon />,
      });
    }

    if (!isPublic) {
      actions.push({
        label: intl.formatMessage({
          id: "action.go_to_file_detail",
          defaultMessage: "File detail page",
        }),
        to: `${window.location.pathname}?${getProjectTabQueryParam(
          "files"
        )}&${convertFileIdToQueryParams(path, ref)}`,
        icon: <FileIcon />,
      });

      if (canWriteProject && isEditable) {
        actions.push({
          label: intl.formatMessage({
            id: "action.set_file_version",
            defaultMessage: "Set file version",
          }),
          action: () => {
            setOpenModal({ type: "file-version", file: fullFile });
          },
          icon: <WrenchIcon />,
        });

        actions.push({
          label: intl.formatMessage({
            id: "reference.change_file_name",
            defaultMessage: "Rename file",
          }),
          action: () => {
            setOpenModal({
              type: "file-reference-name",
              file: fullFile,
            });
          },
          icon: <MdOutlineEdit />,
        });

        actions.push({
          label: intl.formatMessage({
            id: "action.remove_file",
            defaultMessage: "Remove file",
          }),
          action: () => removeClicked([path]),
          icon: <TrashIcon />,
        });
      }
    }

    return actions;
  };

  const getTableActions = (): TableAction[] => {
    const actions: TableAction[] = [];

    if (canWriteProject && isEditable) {
      actions.push({
        label: intl.formatMessage({
          id: "menu.remove_files",
          defaultMessage: "Remove files",
        }),
        action: removeClicked,
        icon: <TrashIcon />,
      });
    }

    actions.push({
      label: intl.formatMessage({
        id: "menu.download_selected_files",
        defaultMessage: "Download selected files",
      }),
      action: async (selectedRowIds) => {
        const selectedFileReferences = publicationFiles.filter(({ path }) =>
          selectedRowIds.includes(path)
        );
        downloadMultipleFileReferences(selectedFileReferences);
      },
      icon: <DownloadIcon />,
    });

    return actions;
  };

  return {
    getRowActions,
    getTableActions,
    onClose: () => setOpenModal(null),
    openModal,
    downloadMultipleFiles,
    openAddFileModal: () => setOpenModal({ type: "file-add" }),
  };
}
